/**
 * RLIImmersionPlayer.jsx — Rapid Lucid Installation player.
 *
 * Full-screen 3-minute cinematic video with:
 *   • Central gold fixation dot (breathes slowly)
 *   • 48 vision-board images pulsing around the dot — progressive
 *     visibility: subliminal early → increasingly visible late
 *   • 24 affirmation phrases crossfading every ~7s beneath the dot
 *   • Cinematic music + 6Hz theta binaural carrier
 *   • Skip locked for the first 90 seconds
 *
 * Independent from the older Sora-2 multi-clip player. Takes a single
 * journey `{title, video_url}` and the dream (for images + phrases).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Play, Pause, Volume2, VolumeX } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TOK = {
  ink:      "#03060A",
  cream:    "#F3EDDD",
  creamDim: "rgba(243, 237, 221, 0.55)",
  gold:     "#A88A4C",
  goldSoft: "#D7BB7A",
  goldMist: "rgba(215, 187, 122, 0.14)",
};

const TOTAL_DURATION = 360;      // 6 minutes — two rounds
const ROUND_1_END = 210;         // Round 1 lasts 3:30
const SKIP_UNLOCK_AT = 90;
const AFFIRMATION_EVERY = 7;
const CRESCENDO_START = 330;     // last 30s of round 2 — final swell

// Image cadence — returns the ms interval between flashes at a given
// elapsed time. Round 1 breathes; Round 2 is torrential — with two
// overlapping streams (see below) so several images can flash at once.
const imageIntervalMs = (t) => {
  if (t < 25)  return 3200;                                // slow open
  if (t < 80)  return 3200 - ((t - 25) / 55) * 2500;       // 3200 → 700 crescendo
  if (t < 150) return 700;                                 // peak round-1
  if (t < 210) return 700 + ((t - 150) / 60) * 1400;       // 700 → 2100 drop back
  if (t < 240) return 2100 - ((t - 210) / 30) * 1800;      // 2100 → 300 lift-off
  return 300;                                              // Round 2 — every 0.3s
};

// Progressive visibility curve — subliminal → clearly visible.
// FLASH DURATION IS SHORT (true subliminal window ~120-260ms) so each
// image registers with the unconscious without loitering on screen.
const visibilityAt = (t) => {
  const p = Math.min(1, Math.max(0, t / TOTAL_DURATION));
  const round2 = t >= ROUND_1_END;
  return {
    opacity:  (round2 ? 0.45 : 0.24) + p * 0.55,
    scale:    0.62 + p * 0.4,
    duration: (round2 ? 70 : 90) + p * 50,       // fast flashes: sensed, not studied (90→140ms R1, 70→120ms R2)
  };
};

// Coloured concentric ripples — the "witches" pulsing outward from
// the dot in round 2 to take the user deeper.
const RIPPLE_COLOURS = [
  "rgba(255, 220, 150, 0.55)", // gold
  "rgba(255, 176, 210, 0.50)", // rose
  "rgba(180, 220, 210, 0.50)", // sage
  "rgba(190, 200, 255, 0.55)", // indigo
  "rgba(255, 246, 220, 0.55)", // pearl
  "rgba(230, 190, 255, 0.55)", // lavender
];

// 8 orbit positions around the central dot (percentage offsets).
const ORBIT = [
  { x: -32, y: -22 }, { x: 32, y: -22 },
  { x: -36, y:  0  }, { x: 36, y:  0  },
  { x: -32, y:  22 }, { x: 32, y:  22 },
  { x:  0,  y: -30 }, { x:  0, y:  30 },
];

// Aurora dust — soft golden motes drifting around the dot the whole
// session. Positions/durations are seeded once so the field feels
// alive but stable.
const AURORA_MOTES = Array.from({ length: 22 }, (_, i) => {
  const angle = (i / 22) * Math.PI * 2;
  const radius = 18 + (i % 4) * 6;       // scattered radial distance
  return {
    key: i,
    x: Math.cos(angle) * radius,          // in vmin
    y: Math.sin(angle) * radius,
    delay: (i * 0.37) % 6,
    duration: 8 + (i % 5) * 1.2,          // 8–13s float cycles
    size: 3 + (i % 3),
  };
});

const absUrl = (u) => (u?.startsWith("http") ? u : `${API}${u}`);
const rampVol = (el, to, ms) => {
  if (!el) return;
  const from = el.volume; const t0 = performance.now();
  clearInterval(el._ramp);
  el._ramp = setInterval(() => {
    const k = Math.min(1, (performance.now() - t0) / ms);
    el.volume = Math.max(0, Math.min(1, from + (to - from) * k));
    if (k >= 1) clearInterval(el._ramp);
  }, 40);
};

const SPACE_NAMES = { atelier: ["The Atelier Immersion", "The Reclaim Atelier"], golf: ["The Golf Immersion", "Reclaim Golf"], abundance: ["The Abundance Immersion", "Reclaim Abundance"],
  presence_morning: ["The Morning Immersion", "Reclaim Presence"], presence_evening: ["The Evening Immersion", "Reclaim Presence"],
  gym: ["The Gym Immersion", "Reclaim Gym"], inner_path: ["The Inner Path Immersion", "Reclaim Inner Path"],
  networker: ["The Networker Immersion", "The Edge"] };

export default function RLIImmersionPlayer({ journey, dream, onClose, coachArea = "atelier", openingOverride = null }) {
  const [kickerName, veilName] = SPACE_NAMES[coachArea] || SPACE_NAMES.atelier;
  // Coach layer (Reclaim Immersion standard): the area's guide opens the door, then returns only to close.
  const coachRef = useRef(null);
  const coachDone = useRef({});
  const coachSpeaking = useRef(false);
  const driftRef = useRef(null);
  const [coach, setCoach] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem("reclaim_token");
    fetch(`${API}/api/immersions/${coachArea}/coach`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => (r.ok ? r.json() : null)).then((c) => setCoach(c && openingOverride ? { ...c, opening: openingOverride } : c))
      .catch(() => openingOverride && setCoach({ opening: openingOverride, mids: [], closing: null }));
    return () => { try { coachRef.current?.pause(); } catch { /* noop */ } };
  }, [coachArea, openingOverride]);
  const coachSay = useCallback((clip) => {
    const a = coachRef.current; const m = musicRef.current;
    if (!a || !clip) return;
    const base = () => musicRef.current?._base ?? 0.55;
    coachSpeaking.current = true;
    rampVol(m, base() * 0.38, 1600);
    a.src = absUrl(clip.audio_url);
    const end = () => { coachSpeaking.current = false; rampVol(musicRef.current, base(), 3500); };
    a.onended = a.onerror = end;
    setTimeout(() => a.play().catch(end), 900);
  }, []);

  const videoRef = useRef(null);
  const musicRef = useRef(null);
  const whispersRef = useRef(null);
  const handoffRef = useRef(null);
  const audioCtxRef = useRef(null);
  const binauralRef = useRef(null);

  const [elapsed, setElapsed] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showOnboard, setShowOnboard] = useState(true);
  const [isCompleting, setIsCompleting] = useState(false);

  const [flashImages, setFlashImages] = useState([]);
  const [sparkles, setSparkles] = useState([]);       // trailing motes when a flash fades
  const [landmarkImage, setLandmarkImage] = useState(null); // occasional big center bloom
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [whispersUrl, setWhispersUrl] = useState(null);

  // Preflight — HEAD-check every audio/video URL before enabling the
  // Begin button. Users never sit through a silent-degraded session
  // again; if any track can't load, we show a friendly retry pill
  // instead of letting them tap Begin and hit an empty immersion.
  //   'idle' — not yet checked
  //   'checking' — HEAD requests in flight
  //   'ready' — every URL returned 200/206
  //   'partial' — some URLs failed; Begin is still allowed but
  //               the failing tracks are surfaced up-front so we
  //               don't ambush the user mid-session
  const [preflight, setPreflight] = useState({ state: "idle", failed: [] });

  // Track health — surfaces silent audio/video failures so users
  // don't sit through an "empty" immersion. Any track that fails to
  // start emits into this map; the header pill turns amber if
  // anything's degraded and admin telemetry captures the source.
  //   music | handoff | video | binaural | whispers
  const [trackHealth, setTrackHealth] = useState({});
  const markTrack = useCallback((key, status, detail = null) => {
    setTrackHealth((prev) => ({ ...prev, [key]: { status, detail, at: Date.now() } }));
    // Fire-and-forget telemetry — helps us catch scale regressions
    // before users complain. Payload is tiny (< 200 bytes). We emit
    // for every diagnostic status (started/error/stalled/recovered)
    // so the admin rollup accurately reflects real-world track
    // health — 'stalled' was under-counted before.
    if (["error", "stalled", "recovered", "started"].includes(status)) {
      try {
        const token = localStorage.getItem("reclaim_token");
        fetch(`${API}/api/atelier/immersion/telemetry`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            dream_id: dream?.id,
            track: key,
            status,
            detail: detail ? String(detail).slice(0, 200) : null,
          }),
          keepalive: true,
        }).catch(() => {});
      } catch { /* ignore */ }
    }
  }, [dream?.id]);
  // Track the colour of the most recent Round 2 ripple so flashes
  // can pick up its wash (unifies the visual field).
  const activeRippleRef = useRef(null);

  // Prepare image + phrase pools ------------------------------------
  // Merge AI-generated vision-board images with the user's personal
  // additions so their own memories/photos pulse alongside the dream.
  // Personal images are duplicated so they appear ~2× more often —
  // the user's own memories/face lead the installation.
  const images = useMemo(() => {
    const ai = (dream?.vision_board || []).filter((i) => i?.url);
    const personal = (dream?.personal_images || []).filter((i) => i?.url);
    const weightedPersonal = [...personal, ...personal]; // 2× weight
    return [...ai, ...weightedPersonal].slice(0, 96);   // room for weighting
  }, [dream]);
  const phrases = useMemo(() => {
    const out = [];
    (dream?.affirmations || []).forEach((a) => {
      const t = a?.text || a?.affirmation;
      if (t) out.push(t);
    });
    return out.slice(0, 24);
  }, [dream]);

  const skipUnlocked = elapsed >= SKIP_UNLOCK_AT;
  // Coach timeline: opening at the start, closing near the end (music ducks underneath).
  useEffect(() => {
    if (!coach || showOnboard) return;
    const d = coachDone.current;
    if (!d.open && elapsed >= 1 && elapsed < 30) { d.open = true; coachSay(coach.opening); }
    const closeAt = TOTAL_DURATION - (coach.closing?.duration || 14) - 8;
    if (!d.close && elapsed >= closeAt) { d.close = true; coachSay(coach.closing); }
  }, [elapsed, coach, showOnboard, coachSay]);
  // The Coach pauses and mutes with the rest of the Immersion.
  useEffect(() => {
    const a = coachRef.current;
    if (!a) return;
    a.muted = isMuted;
    if (isPaused) a.pause();
    else if (coachSpeaking.current && a.paused && a.currentTime > 0 && !a.ended) a.play().catch(() => {});
  }, [isPaused, isMuted]);
  // The dot (and the images around it) drift slowly — follow rather than focus.
  useEffect(() => {
    let raf; const t0 = performance.now(); const tau = Math.PI * 2;
    const tick = (now) => {
      const t = (now - t0) / 1000; const W = window.innerWidth; const H = window.innerHeight;
      const amp = Math.min(1, t / 20);
      const x = amp * W * (W < 640 ? 0.2 : 0.17) * (Math.sin((t * tau) / 47) + 0.35 * Math.sin((t * tau) / 19));
      const y = amp * H * 0.085 * (Math.sin((t * tau) / 31 + 1) + 0.3 * Math.sin((t * tau) / 13));
      if (driftRef.current) driftRef.current.style.transform = `translate3d(${x.toFixed(1)}px,${y.toFixed(1)}px,0)`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  const remainingLock = Math.max(0, SKIP_UNLOCK_AT - Math.floor(elapsed));
  const hasIntro = !!journey?.has_intro_audio;
  const introActive = false;             // no journey today ships with embedded intro audio
  const introRemaining = 0;
  const isRound2 = elapsed >= ROUND_1_END;

  // Golden-bloom transition — fires once the moment the guided intro
  // hands off to the subliminal deck. Purely visual (1.6s ripple).
  const [bloomAt, setBloomAt] = useState(null);
  const prevIntroActiveRef = useRef(hasIntro);
  useEffect(() => {
    if (prevIntroActiveRef.current && !introActive && !showOnboard) {
      setBloomAt(Date.now());
      const t = setTimeout(() => setBloomAt(null), 1800);
      return () => clearTimeout(t);
    }
    prevIntroActiveRef.current = introActive;
  }, [introActive, showOnboard]);

  // Crescendo — dot bloom + music swells in the last 30s of the ritual.
  // Smooth 0..1 ramp from CRESCENDO_START to TOTAL_DURATION.
  const crescendo = Math.min(
    1,
    Math.max(0, (elapsed - CRESCENDO_START) / (TOTAL_DURATION - CRESCENDO_START))
  );

  // Convergence phase — the 30 seconds before Round 2 begins.
  // Aurora dust drifts inward toward the dot (a "gasp inhale") and
  // then explodes back outward at t=ROUND_1_END synchronised with
  // the first coloured ripple (the "exhale" release).
  const convergencePhase =
    elapsed >= ROUND_1_END - 30 && elapsed < ROUND_1_END;

  // Exhale burst — fires exactly once as Round 2 opens. A big soft
  // gold bloom radiating from the dot, marking the release.
  const [exhaleAt, setExhaleAt] = useState(null);
  const prevConvergingRef = useRef(false);
  useEffect(() => {
    if (prevConvergingRef.current && !convergencePhase && elapsed >= ROUND_1_END) {
      setExhaleAt(Date.now());
      // Fire the guided handoff voice — "going deeper". Duck the
      // music briefly so the voice lands cleanly on top, then let
      // the audio effect restore Round 2 levels naturally.
      try {
        if (musicRef.current) musicRef.current.volume = 0.28;
        if (handoffRef.current) {
          handoffRef.current.currentTime = 0;
          handoffRef.current.play().catch(() => {});
        }
      } catch { /* noop */ }
      const t = setTimeout(() => setExhaleAt(null), 2200);
      prevConvergingRef.current = false;
      return () => clearTimeout(t);
    }
    prevConvergingRef.current = convergencePhase;
  }, [convergencePhase, elapsed]);

  // Close (with completion veil) ------------------------------------
  const handleClose = () => {
    if (showOnboard) { onClose?.(); return; }
    setIsCompleting(true);
    setIsPaused(true);
    setIsMuted(true);
    setTimeout(() => onClose?.(), 2200);
  };

  // Robust play: retry once on failure with a short wait, and mark
  // the track's health either way. Silent .catch(() => {}) was
  // exactly the pattern that made the "no music, only binaural"
  // regression invisible to us for days.
  const robustPlay = useCallback(async (audio, key) => {
    if (!audio) return false;
    const attempt = () => audio.play();
    try {
      await attempt();
      markTrack(key, "started");
      return true;
    } catch (e1) {
      // One retry after a very short wait — often enough on flaky
      // networks or when the media source hasn't fully buffered.
      await new Promise((r) => setTimeout(r, 250));
      try {
        await attempt();
        markTrack(key, "recovered", `retried after: ${e1?.name || e1}`);
        return true;
      } catch (e2) {
        markTrack(key, "error", `${e2?.name || e2}`);
        return false;
      }
    }
  }, [markTrack]);

  // Preflight check — runs once when the onboard screen mounts. HEAD
  // (or a tiny Range GET as fallback) every audio + video URL the
  // immersion needs. If any come back < 200 or > 299, we surface the
  // failure BEFORE the user taps Begin so they never sit through a
  // silent session. This is the direct anti-recurrence safeguard for
  // Nanna's "video degraded" and "no music" bugs — instead of the
  // player discovering the failure mid-session, we discover it up
  // front and offer a friendly retry.
  useEffect(() => {
    if (!showOnboard) return;
    if (preflight.state !== "idle") return;
    if (!journey) return;

    let cancelled = false;
    setPreflight({ state: "checking", failed: [] });

    const checkOne = async (key, url) => {
      if (!url) return { key, ok: true }; // treat missing URL as "not required"
      // Hard 5s timeout per check — never let the Begin button stay
      // in "Preparing…" forever if a HEAD stalls (which is exactly
      // what Michael just hit on iPhone — Cloudflare edge held the
      // request open on a healthy endpoint).
      const ctl = new AbortController();
      const timer = setTimeout(() => ctl.abort(), 5000);
      try {
        // HEAD first — cheap and doesn't warm CDN cache with big video.
        const r = await fetch(url, { method: "HEAD", cache: "no-store", signal: ctl.signal });
        clearTimeout(timer);
        if (r.ok || r.status === 206) return { key, ok: true, size: r.headers.get("content-length") };
        // Some CDNs return 405 to HEAD but 206 to Range. Fall back.
        if (r.status === 405) {
          const ctl2 = new AbortController();
          const timer2 = setTimeout(() => ctl2.abort(), 5000);
          try {
            const r2 = await fetch(url, { method: "GET", headers: { Range: "bytes=0-1023" }, cache: "no-store", signal: ctl2.signal });
            clearTimeout(timer2);
            return { key, ok: r2.ok || r2.status === 206, status: r2.status };
          } catch (e2) {
            clearTimeout(timer2);
            return { key, ok: false, status: "timeout", detail: String(e2).slice(0, 80) };
          }
        }
        return { key, ok: false, status: r.status };
      } catch (e) {
        clearTimeout(timer);
        return { key, ok: false, status: "timeout", detail: String(e).slice(0, 80) };
      }
    };

    const videoUrl = journey?.video_url ? `${API}${journey.video_url}` : null;
    const musicUrl = `${API}/api/audio/ambient/healing.mp3`;
    const handoffUrl = `${API}/api/atelier/handoff-audio/deeper.mp3`;

    (async () => {
      const results = await Promise.all([
        checkOne("video",   videoUrl),
        checkOne("music",   musicUrl),
        checkOne("handoff", handoffUrl),
      ]);
      if (cancelled) return;
      const failed = results.filter((r) => !r.ok).map((r) => ({ key: r.key, status: r.status || "unknown" }));
      if (failed.length === 0) {
        setPreflight({ state: "ready", failed: [] });
      } else {
        // Non-blocking — user can still tap Begin (network may recover),
        // but we've now surfaced the risk so they're not blindsided.
        setPreflight({ state: "partial", failed });
        // Also emit telemetry so admins see preflight failures rolling in.
        for (const f of failed) markTrack(f.key, "error", `preflight:${f.status}`);
      }
    })();

    return () => { cancelled = true; };
  }, [showOnboard, preflight.state, journey, markTrack]);

  // Begin — start video + music + audio context in the SAME
  // synchronous user gesture so browser autoplay policies respect us.
  const handleBegin = () => {
    // Music bed — instantiate + play synchronously here so Chromium/
    // Safari attach the click gesture to this Audio element.
    // NOTE: no crossOrigin — iOS Safari refuses to play `crossOrigin
    // anonymous` audio when the server response omits the CORS ACAO
    // header on ranged requests (which our audio_range endpoint
    // sometimes does). Since we do not run this audio through Web
    // Audio API, plain-origin is the correct setting.
    try {
      if (!musicRef.current) {
        const a = new Audio(`${API}/api/audio/ambient/healing.mp3`);
        a.loop = true;
        a.volume = 0.55;
        // Any subsequent stall (network drop) marks the track as
        // errored so the user sees an amber pill rather than silence.
        a.addEventListener("stalled", () => markTrack("music", "stalled"));
        a.addEventListener("error",   () => markTrack("music", "error", a.error?.message));
        a.addEventListener("playing", () => markTrack("music", "started"));
        musicRef.current = a;
      }
      robustPlay(musicRef.current, "music");
    } catch (e) { markTrack("music", "error", e?.message); }
    // Coach voice — primed inside the same tap so it can speak later on iPhone.
    try {
      if (!coachRef.current) {
        const c = new Audio("/silence.mp3");
        coachRef.current = c;
        c.play().catch(() => {});
      }
    } catch { /* noop */ }
    // Round 2 handoff cue — warm-primed inside the same user
    // gesture (play → immediate pause) so it can .play() at
    // t=ROUND_1_END without needing a fresh click.
    try {
      if (!handoffRef.current) {
        const h = new Audio(`${API}/api/atelier/handoff-audio/deeper.mp3`);
        h.volume = 0.85;
        h.preload = "auto";
        h.addEventListener("error", () => markTrack("handoff", "error", h.error?.message));
        handoffRef.current = h;
        h.play().then(() => { h.pause(); markTrack("handoff", "primed"); }).catch((e) => markTrack("handoff", "error", e?.message));
      }
    } catch (e) { markTrack("handoff", "error", e?.message); }
    // Prime the AudioContext for the binaural carrier — iOS
    // requires this to happen inside the gesture.
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!audioCtxRef.current && Ctx) {
        audioCtxRef.current = new Ctx();
      }
      audioCtxRef.current?.resume?.().catch(() => {});
    } catch { /* noop */ }
    setShowOnboard(false);
    try {
      const v = videoRef.current;
      if (v) {
        if (hasIntro) v.muted = false;
        v.addEventListener("stalled", () => markTrack("video", "stalled"), { once: true });
        v.addEventListener("error",   () => markTrack("video", "error", v.error?.message), { once: true });
        v.addEventListener("playing", () => markTrack("video", "started"), { once: true });
        v.play?.().catch((e) => markTrack("video", "error", e?.message));
      }
    } catch (e) { markTrack("video", "error", e?.message); }
  };

  // Elapsed timer — driven by wall-clock so it stays accurate even
  // when iOS Safari throttles or pauses requestAnimationFrame (e.g.
  // during link-preview mode, briefly-locked screen, notifications
  // or backgrounding). We combine:
  //   • RAF loop  — smooth, primary
  //   • 1s setInterval failsafe — catches RAF stalls
  //   • visibilitychange listener — force-recompute on tab resume
  // The three mechanisms all read `startedAt` from the effect closure
  // and compute `next = floor((Date.now() - startedAt) / 1000)`.
  // Whichever fires first updates `elapsed`; the others no-op if the
  // value hasn't advanced. This fixes the "STAY · Ns" freeze bug
  // reported Feb 13 2026 · Michael's iOS Safari session.
  useEffect(() => {
    if (showOnboard || isPaused) return;
    const startedAt = Date.now() - elapsed * 1000;
    let rafId = null;
    let intervalId = null;
    let closed = false;
    let lastSecond = elapsed;
    const advance = () => {
      if (closed) return;
      const next = Math.floor((Date.now() - startedAt) / 1000);
      if (next !== lastSecond) {
        lastSecond = next;
        setElapsed(next);
        if (next >= TOTAL_DURATION) {
          closed = true;
          if (rafId) cancelAnimationFrame(rafId);
          if (intervalId) clearInterval(intervalId);
          setTimeout(() => handleClose(), 400);
          return;
        }
      }
    };
    const tick = () => {
      advance();
      if (closed) return;
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    // Failsafe: even if RAF gets throttled to 0Hz, this fires every
    // 1s from the browser's timer queue. On iOS Safari, timers still
    // fire (typically at 1s minimum) even when RAF is paused.
    intervalId = setInterval(advance, 1000);
    // Catch-up: when Safari resumes the tab, immediately recompute.
    const onVis = () => { if (!document.hidden) advance(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      closed = true;
      if (rafId) cancelAnimationFrame(rafId);
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [showOnboard, isPaused]);

  // Image flash scheduler — uses a variable interval so the density
  // breathes: slow open → crescendo → drop back at end of round 1
  // → explosive plentiful round 2. Round 2 also runs a SECOND stream
  // offset by half the interval, so 2 images can be on screen at
  // once — filling more of the peripheral vision without any single
  // image staying long enough to leave the subliminal window.
  const elapsedRef = useRef(0);
  useEffect(() => { elapsedRef.current = elapsed; }, [elapsed]);

  const spawnFlash = React.useCallback((idxRef, orbitOffset = 0) => {
    if (!images.length) return;
    const img = images[idxRef.n % images.length];
    const t = elapsedRef.current;
    const vis = visibilityAt(t);
    idxRef.n += 1;
    const round2 = t >= ROUND_1_END;
    // Round 1 stays on the 8-point orbit. Round 2 breaks free — 60%
    // of flashes appear at random points across the screen to fill
    // peripheral vision, 40% still orbit around the dot.
    let xVmin, yVmin;
    if (round2 && Math.random() < 0.6) {
      xVmin = (Math.random() - 0.5) * 62;   // -31..+31 vmin
      yVmin = (Math.random() - 0.5) * 52;   // -26..+26 vmin
    } else {
      const orbit = ORBIT[(idxRef.n + orbitOffset) % ORBIT.length];
      xVmin = orbit.x;
      yVmin = orbit.y;
    }
    // Coloured tint — Round 2 flashes pick up the wash of whatever
    // ripple is currently on screen. Falls back to gold.
    const tint = (round2 && activeRippleRef.current) || "rgba(215, 187, 122, 1)";
    const key = `${Date.now()}-${Math.random()}`;
    setFlashImages((list) => {
      const capped = list.length > 8 ? list.slice(-8) : list;
      return [...capped, { ...img, x: xVmin, y: yVmin, tint, ...vis, key }];
    });
    setTimeout(() => {
      setFlashImages((list) => list.filter((f) => f.key !== key));
      // Sparkle trail — 2 tiny motes drift outward from where the
      // image was. Only in Round 2 (Round 1 keeps the frame quiet).
      // Bounded so we never accumulate dead nodes on iOS Safari.
      if (round2) {
        const trail = Array.from({ length: 2 }, (_, i) => ({
          key: `${key}-s${i}`,
          x: xVmin + (Math.random() - 0.5) * 4,
          y: yVmin + (Math.random() - 0.5) * 4,
          dx: (Math.random() - 0.5) * 6,
          dy: (Math.random() - 0.5) * 6,
        }));
        setSparkles((s) => [...s.slice(-40), ...trail]);
        setTimeout(() => {
          setSparkles((s) => s.filter((sp) => !trail.some((t) => t.key === sp.key)));
        }, 700);
      }
    }, vis.duration);
  }, [images]);

  useEffect(() => {
    if (showOnboard || isPaused || images.length === 0) return;
    const idxRef = { n: 0 };
    let cancelled = false;
    let timerA = null;
    let timerB = null;
    const tickA = () => {
      if (cancelled) return;
      spawnFlash(idxRef, 0);
      timerA = setTimeout(tickA, imageIntervalMs(elapsedRef.current));
    };
    // Second stream — only active in Round 2. Runs offset from A so
    // two images are typically on screen together.
    const tickB = () => {
      if (cancelled) return;
      if (elapsedRef.current >= ROUND_1_END) {
        spawnFlash(idxRef, 3);  // opposite-side orbit position
      }
      const next = Math.max(300, imageIntervalMs(elapsedRef.current));
      timerB = setTimeout(tickB, next);
    };
    timerA = setTimeout(tickA, 800);
    timerB = setTimeout(tickB, 950 + 200);  // half-cycle offset
    return () => {
      cancelled = true;
      if (timerA) clearTimeout(timerA);
      if (timerB) clearTimeout(timerB);
    };
  }, [showOnboard, isPaused, images.length, spawnFlash]);

  // Phrase rotation --------------------------------------------------
  useEffect(() => {
    if (showOnboard || isPaused || phrases.length === 0) return;
    const t = setInterval(() => {
      setPhraseIdx((i) => (i + 1) % phrases.length);
    }, AFFIRMATION_EVERY * 1000);
    return () => clearInterval(t);
  }, [showOnboard, isPaused, phrases.length]);

  // Coloured concentric ripples — round 2 only. Every 2.4s a new
  // ring spawns from the dot cycling six colours. In the final 30s
  // (`CRESCENDO_START`) they fire every 0.6s creating a rainbow
  // spiral — the dream being installed at maximum saturation.
  const [ripples, setRipples] = useState([]);
  useEffect(() => {
    if (showOnboard || isPaused || elapsed < ROUND_1_END) return;
    let idx = 0;
    let cancelled = false;
    let timerId = null;
    const RIPPLE_LIFE_MS = 4000;
    const spawn = () => {
      if (cancelled) return;
      const key = Date.now() + Math.random();
      const colour = RIPPLE_COLOURS[idx % RIPPLE_COLOURS.length];
      idx += 1;
      // Track the current ripple colour so flash tinting picks it up.
      activeRippleRef.current = colour;
      setRipples((r) => [...r, { key, colour }]);
      setTimeout(() => setRipples((r) => r.filter((x) => x.key !== key)), RIPPLE_LIFE_MS);
      const t = elapsedRef.current;
      const nextIn = t >= CRESCENDO_START ? 600 : 2400;
      timerId = setTimeout(spawn, nextIn);
    };
    timerId = setTimeout(spawn, 200);
    return () => { cancelled = true; if (timerId) clearTimeout(timerId); };
  }, [showOnboard, isPaused, elapsed >= ROUND_1_END]);

  // Landmark bloom — every 18-22s in Round 2 only, a random image
  // blooms center-screen at 2× size for ~500ms. Conscious lock-in
  // moment. Held for Round 2 so it feels earned.
  useEffect(() => {
    if (showOnboard || isPaused || images.length === 0) return;
    let cancelled = false;
    let timer = null;
    const spawn = () => {
      if (cancelled) return;
      if (elapsedRef.current >= ROUND_1_END) {
        const img = images[Math.floor(Math.random() * images.length)];
        const key = `landmark-${Date.now()}`;
        setLandmarkImage({ ...img, key });
        setTimeout(() => setLandmarkImage((c) => (c?.key === key ? null : c)), 550);
      }
      timer = setTimeout(spawn, 18000 + Math.random() * 4000);
    };
    timer = setTimeout(spawn, 25000);   // first landmark ~25s into round 2
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [showOnboard, isPaused, images.length]);

  // Shooting-star trails — round 2 only. A bright streak sweeps
  // across the screen every ~4-6 seconds at a random angle and
  // height. Purely visual — makes the deeper wave feel like the
  // heavens are opening.
  const [streaks, setStreaks] = useState([]);
  useEffect(() => {
    if (showOnboard || isPaused || elapsed < ROUND_1_END) return;
    let cancelled = false;
    let timer = null;
    const STREAK_LIFE_MS = 1200;
    const spawn = () => {
      if (cancelled) return;
      const key = Date.now() + Math.random();
      // Random top position (10%-70%), random horizontal direction
      const top = 12 + Math.random() * 58;
      const rightToLeft = Math.random() > 0.5;
      const angle = (Math.random() - 0.5) * 20;    // -10° to +10°
      setStreaks((s) => [...s, { key, top, rightToLeft, angle }]);
      setTimeout(() => setStreaks((s) => s.filter((x) => x.key !== key)), STREAK_LIFE_MS);
      timer = setTimeout(spawn, 3800 + Math.random() * 2400);
    };
    timer = setTimeout(spawn, 900);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [showOnboard, isPaused, elapsed >= ROUND_1_END]);

  // Audio (music + binaural) ----------------------------------------
  useEffect(() => {
    if (showOnboard) return;
    if (isPaused || isMuted) {
      try { musicRef.current?.pause(); } catch { /* noop */ }
      try {
        binauralRef.current?.oscL?.stop(0);
        binauralRef.current?.oscR?.stop(0);
      } catch { /* noop */ }
      binauralRef.current = null;
      return;
    }
    // Cinematic bed — the `healing.mp3` track works reliably (the
    // legacy `cinematic_*.mp3` files were disk-offloaded and now
    // return 400 in prod). Volume sits high enough to carry the
    // immersion but low enough that binaural + whispers still layer
    // cleanly on top.
    try {
      if (!musicRef.current) {
        musicRef.current = new Audio(`${API}/api/audio/ambient/healing.mp3`);
        musicRef.current.loop = true;
      }
      // Round 2 lifts the music slightly so the deeper wave feels
      // fuller under the ripples + torrent of images.
      musicRef.current._base = hasIntro ? (introActive ? 0.22 : 0.45) : (isRound2 ? 0.72 : 0.55);
      clearInterval(musicRef.current._ramp);
      musicRef.current.volume = musicRef.current._base * (coachSpeaking.current ? 0.38 : 1);
      musicRef.current.play().catch(() => {});
    } catch { /* noop */ }
    // Binaural theta 6Hz carrier — quieter under a guided-voice video.
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!audioCtxRef.current && Ctx) audioCtxRef.current = new Ctx();
      const ctx = audioCtxRef.current;
      if (ctx && !binauralRef.current) {
        const merger = ctx.createChannelMerger(2);
        const oscL = ctx.createOscillator(); oscL.frequency.value = 174;
        const oscR = ctx.createOscillator(); oscR.frequency.value = 180; // +6Hz
        const gain = ctx.createGain();
        gain.gain.value = hasIntro ? 0.025 : 0.05;
        oscL.connect(merger, 0, 0);
        oscR.connect(merger, 0, 1);
        merger.connect(gain).connect(ctx.destination);
        oscL.start(); oscR.start();
        binauralRef.current = { oscL, oscR, gain };
      }
    } catch { /* noop */ }
    return () => {
      try { musicRef.current?.pause(); } catch { /* noop */ }
      try {
        binauralRef.current?.oscL?.stop(0);
        binauralRef.current?.oscR?.stop(0);
      } catch { /* noop */ }
      binauralRef.current = null;
    };
  }, [showOnboard, isPaused, isMuted, hasIntro, introActive, isRound2]);

  // Pause / resume video with state; also honour mute toggle when the
  // video carries embedded audio (intro voice).
  useEffect(() => {
    const v = videoRef.current;
    if (!v || showOnboard) return;
    if (isPaused) v.pause();
    else v.play().catch(() => {});
    if (hasIntro) v.muted = isMuted;
  }, [isPaused, isMuted, showOnboard, hasIntro]);

  // Whisper track — the dream's affirmations spoken faintly, looped
  // beneath the music at very low volume. Server renders lazily on
  // first hit; we poll until ready then swap the URL in.
  useEffect(() => {
    if (showOnboard || !dream?.id) return;
    let cancelled = false;
    let pollTimer = null;
    let objectUrl = null;
    const token = localStorage.getItem("reclaim_token");
    const authFetch = (path, init = {}) =>
      fetch(`${API}${path}`, {
        ...init,
        headers: {
          ...(init.headers || {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
    const kick = async () => {
      try {
        if (!dream?.id) return;
        const r = await authFetch(`/api/atelier/dreams/${dream.id}/whispers/status`);
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        if (data.status === "ready" && data.url) {
          // Auth-fetch the mp3 as a blob so the <audio> element can
          // play it without needing to send Authorization headers.
          const audioResp = await authFetch(data.url);
          if (!audioResp.ok || cancelled) return;
          const blob = await audioResp.blob();
          objectUrl = URL.createObjectURL(blob);
          if (!cancelled) setWhispersUrl(objectUrl);
        } else if (data.status === "building") {
          pollTimer = setTimeout(kick, 8000);
        }
      } catch { /* whispers are a nice-to-have */ }
    };
    kick();
    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [showOnboard, dream?.id]);

  // Play the whisper track once available — at a very faint volume so
  // it slips under the music/binaural. The server bakes -14 dB of
  // additional attenuation into the file itself.
  useEffect(() => {
    if (showOnboard || !whispersUrl) return;
    if (isPaused || isMuted) {
      try { whispersRef.current?.pause(); } catch { /* noop */ }
      return;
    }
    try {
      if (!whispersRef.current || whispersRef.current._src !== whispersUrl) {
        const a = new Audio(whispersUrl);
        a.loop = true;
        a._src = whispersUrl;
        a.addEventListener("error",   () => markTrack("whispers", "error", a.error?.message));
        a.addEventListener("stalled", () => markTrack("whispers", "stalled"));
        a.addEventListener("playing", () => markTrack("whispers", "started"));
        whispersRef.current = a;
      }
      // Whispers of the affirmations. Baked -14 dB at the file
      // level; playing at 0.5 in Round 1 (~ -20 dB effective) sits
      // subconsciously under the music. Round 2 lifts to 0.78 so
      // the dream *emerges* from beneath and speaks to the user
      // directly as the deeper wave arrives.
      whispersRef.current.volume = isRound2 ? 0.78 : 0.5;
      whispersRef.current.play().catch((e) => markTrack("whispers", "error", e?.message));
    } catch (e) { markTrack("whispers", "error", e?.message); }
    return () => {
      try { whispersRef.current?.pause(); } catch { /* noop */ }
    };
  }, [whispersUrl, showOnboard, isPaused, isMuted, isRound2, markTrack]);

  const currentPhrase = introActive ? null : phrases[phraseIdx];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] overflow-hidden"
      style={{ background: TOK.ink }}
      data-testid={showOnboard ? "rli-immersion-shell" : "rli-immersion-player"}
    >
      {/* Cinematic video backdrop — ALWAYS mounted (even during
          onboard) so its ref is available inside the user-gesture
          window when Begin is tapped. Starts muted so autoplay is
          allowed; handleBegin() unmutes it inside the click.
          Slow dream-zoom: 1.0× → 1.08× over 6 min, giving a subtle
          "descending into the dream" feeling by the end. */}
      <video
        ref={videoRef}
        src={`${API}${journey?.video_url || ""}`}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          opacity: showOnboard ? 0 : 0.85,
          transform: `scale(${1 + Math.min(1, elapsed / TOTAL_DURATION) * 0.08})`,
          transition: "opacity 900ms ease, transform 1200ms linear",
        }}
      />

      {/* Track-degraded pill — surfaces silent audio/video failures
          so users never sit through an "empty" immersion again. Only
          visible when at least one track has stalled or errored. */}
      {!showOnboard && (() => {
        const degraded = Object.entries(trackHealth).filter(
          ([, v]) => v?.status === "error" || v?.status === "stalled",
        );
        if (degraded.length === 0) return null;
        return (
          <div
            className="absolute z-[120] pointer-events-none"
            style={{ top: "16px", left: "50%", transform: "translateX(-50%)" }}
            data-testid="rli-track-degraded-pill"
          >
            <div
              className="px-3 py-1.5 rounded-full text-[10px] tracking-[0.18em] uppercase"
              style={{
                background: "rgba(20, 12, 6, 0.72)",
                color: "#F4C67C",
                border: "1px solid rgba(244, 198, 124, 0.35)",
                backdropFilter: "blur(6px)",
              }}
            >
              {degraded.map(([k]) => k).join(" · ")} degraded
            </div>
          </div>
        );
      })()}

      {/* Onboard veil — sits ABOVE the video while it primes */}
      <AnimatePresence>
        {showOnboard && (
          <motion.div
            key="rli-onboard"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="absolute inset-0 z-[10] flex flex-col items-center justify-center px-6"
            style={{ background: TOK.ink }}
            data-testid="rli-immersion-onboard"
          >
            <p
              className="uppercase tracking-[0.4em] mb-5"
              style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif', fontSize: "0.62rem" }}
            >
              {kickerName}
            </p>
            <h1
              className="italic text-center mb-3"
              style={{
                color: TOK.cream,
                fontFamily: '"Cormorant Garamond", serif',
                fontWeight: 400,
                fontSize: "clamp(1.6rem, 4.5vw, 2.4rem)",
                lineHeight: 1.2,
                maxWidth: "32rem",
              }}
            >
              {journey?.title || "Loading…"}
            </h1>
            <p
              className="italic text-center mb-10 opacity-80"
              style={{ color: TOK.cream, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.05rem", maxWidth: "28rem" }}
            >
              Soften your eyes onto the gold dot. Let your vision widen. Let it in.
            </p>
            <p
              className="text-center italic mb-8 opacity-70"
              style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif', fontSize: "0.94rem", maxWidth: "30rem" }}
            >
              Best with headphones in a quiet room. Six minutes, in two waves.
            </p>
            <button
              onClick={handleBegin}
              className="px-9 py-3.5 rounded-full uppercase transition-all hover:scale-[1.03]"
              style={{
                background: "rgba(215, 187, 122, 0.18)",
                border: `1px solid ${TOK.gold}`,
                color: TOK.cream,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.72rem",
                letterSpacing: "0.28em",
              }}
              data-testid="rli-immersion-begin"
            >
              Begin
            </button>
            {preflight.state === "checking" && (
              <p
                className="mt-3 text-center text-[10px] tracking-[0.28em] uppercase opacity-50"
                style={{ color: TOK.creamDim }}
                data-testid="rli-immersion-preflight-checking"
              >
                Warming your immersion…
              </p>
            )}
            {preflight.state === "partial" && (
              <p
                className="mt-4 text-center text-[11px] tracking-[0.18em] uppercase"
                style={{ color: "#F4C67C", maxWidth: "22rem", opacity: 0.85 }}
                data-testid="rli-immersion-preflight-warn"
              >
                {preflight.failed.map((f) => f.key).join(" · ")} slow to load — you can still Begin, we&apos;ll retry as you go.
              </p>
            )}
            <button
              onClick={onClose}
              className="absolute top-6 right-6 w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: "rgba(15, 27, 23, 0.6)", border: `1px solid ${TOK.goldMist}`, color: TOK.creamDim }}
              aria-label="Close"
              data-testid="rli-immersion-onboard-close"
            >
              <X size={16} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Player-only chrome (renders under the onboard veil while
          onboard is showing; visible once the user taps Begin) */}

      {/* Soft vignette so the dot + flashes pop. Tightens gently
          during the crescendo, and breathes on a 10 s cycle (4-in /
          6-out) that users unconsciously entrain to. */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        aria-hidden
        animate={{
          background: [
            `radial-gradient(ellipse at center, transparent ${40 - crescendo * 12}%, rgba(3,6,10,${0.55 + crescendo * 0.25}) 100%)`,
            `radial-gradient(ellipse at center, transparent ${46 - crescendo * 12}%, rgba(3,6,10,${0.45 + crescendo * 0.25}) 100%)`,
            `radial-gradient(ellipse at center, transparent ${38 - crescendo * 12}%, rgba(3,6,10,${0.60 + crescendo * 0.25}) 100%)`,
          ],
        }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", times: [0, 0.4, 1] }}
      />

      <div ref={driftRef} className="absolute inset-0 pointer-events-none" style={{ willChange: "transform" }} data-testid="rli-drift">
      {/* Vision-board flashes — 1 image in Round 1, up to 2 overlapping
          streams in Round 2, orbiting the dot or scattered across the
          whole screen. Short duration (140-360 ms) keeps each image
          in the subliminal window. Round 2 flashes pick up the wash
          of the currently-active coloured ripple. */}
      <AnimatePresence>
        {flashImages.map((flashImage) => (
          <motion.img
            key={flashImage.key}
            src={absUrl(flashImage.url)}
            alt=""
            initial={{ opacity: 0, scale: flashImage.scale * 0.96 }}
            animate={{ opacity: flashImage.opacity, scale: flashImage.scale }}
            exit={{ opacity: 0, scale: flashImage.scale * 1.03, transition: { duration: 0.06 } }}
            transition={{ duration: 0.05 }}
            className="absolute pointer-events-none rounded-2xl object-cover"
            style={{
              top: `calc(50% + ${flashImage.y}vmin)`,
              left: `calc(50% + ${flashImage.x}vmin)`,
              transform: "translate(-50%, -50%)",
              width: "26vmin",
              height: "26vmin",
              boxShadow: `0 0 60px ${flashImage.tint.replace(/[\d.]+\)$/, `${0.15 + flashImage.opacity * 0.5})`)}`,
              mixBlendMode: "screen",
            }}
            data-testid="rli-immersion-flash-image"
          />
        ))}
      </AnimatePresence>

      {/* Sparkle trails — 4 tiny motes drift outward from each faded
          flash. Brief afterimage as shimmer. */}
      <AnimatePresence>
        {sparkles.map((s) => (
          <motion.span
            key={s.key}
            aria-hidden
            className="absolute pointer-events-none rounded-full"
            style={{
              left: `calc(50% + ${s.x}vmin)`,
              top: `calc(50% + ${s.y}vmin)`,
              width: "3px",
              height: "3px",
              background: "radial-gradient(circle, rgba(255,246,220,1) 0%, rgba(215,187,122,0.6) 60%, rgba(215,187,122,0) 100%)",
              boxShadow: "0 0 8px rgba(255, 236, 190, 0.9)",
              mixBlendMode: "screen",
            }}
            initial={{ opacity: 0.95, x: 0, y: 0, scale: 1 }}
            animate={{ opacity: 0, x: `${s.dx}vmin`, y: `${s.dy}vmin`, scale: 0.4 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          />
        ))}
      </AnimatePresence>

      {/* Landmark bloom — every ~20s a random image blooms center-
          screen at 2× size for ~400ms then dissolves. Consciously
          registered lock-in moment amid the subliminal stream. */}
      <AnimatePresence>
        {landmarkImage && (
          <motion.img
            key={landmarkImage.key}
            src={`${API}${landmarkImage.url}`}
            alt=""
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 0.82, scale: 1 }}
            exit={{ opacity: 0, scale: 1.1 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none rounded-2xl object-cover"
            style={{
              width: "52vmin",
              height: "52vmin",
              boxShadow: "0 0 120px rgba(255, 236, 190, 0.8), 0 0 220px rgba(215, 187, 122, 0.55)",
              mixBlendMode: "screen",
            }}
            data-testid="rli-immersion-landmark"
          />
        )}
      </AnimatePresence>

      {/* Aurora dust — 22 soft golden motes drifting around the dot.
          Alive but subtle. In the last 30 s of Round 1 they collapse
          inward toward the dot (the "gasp inhale"), then explode
          back outward as Round 2 begins (the "exhale" — synchronised
          with the first coloured ripple). */}
      <motion.div
        className="absolute left-1/2 top-1/2 pointer-events-none"
        data-testid="rli-immersion-aurora"
        animate={{
          scale: convergencePhase ? 0.18 : 1,
          opacity: convergencePhase ? [0.85, 1] : 1,
        }}
        transition={{
          scale: {
            duration: convergencePhase ? 30 : 1.4,
            ease: convergencePhase ? [0.4, 0, 0.6, 1] : [0.16, 1, 0.3, 1],
          },
          opacity: { duration: convergencePhase ? 30 : 1.4 },
        }}
      >
        {AURORA_MOTES.map((m) => (
          <motion.span
            key={m.key}
            aria-hidden
            className="absolute rounded-full"
            style={{
              left: `${m.x}vmin`,
              top: `${m.y}vmin`,
              width: `${m.size}px`,
              height: `${m.size}px`,
              background: "radial-gradient(circle, rgba(255,236,190,0.95) 0%, rgba(215,187,122,0.6) 45%, rgba(215,187,122,0) 100%)",
              boxShadow: "0 0 12px rgba(215, 187, 122, 0.65)",
            }}
            animate={{
              opacity: [0.15, 0.85, 0.15],
              x: [0, (m.key % 2 ? 1 : -1) * 12, 0],
              y: [0, (m.key % 3 - 1) * 10, 0],
              scale: [0.85, 1.3, 0.85],
            }}
            transition={{
              duration: m.duration,
              repeat: Infinity,
              ease: "easeInOut",
              delay: m.delay,
            }}
          />
        ))}
      </motion.div>

      {/* Transition bloom — fires the moment the guided intro hands
          off to the subliminal deck. A soft ripple radiating out of
          the dot marks the shift from "being led" to "being installed". */}
      <AnimatePresence>
        {bloomAt && (
          <motion.div
            key={bloomAt}
            aria-hidden
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none rounded-full"
            style={{
              width: "40px",
              height: "40px",
              background: "radial-gradient(circle, rgba(255,236,190,0.9) 0%, rgba(215,187,122,0.55) 35%, rgba(215,187,122,0) 75%)",
              mixBlendMode: "screen",
            }}
            initial={{ scale: 0.4, opacity: 0.0 }}
            animate={{ scale: 18, opacity: 0.9 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
            data-testid="rli-immersion-bloom"
          />
        )}
      </AnimatePresence>

      {/* Shooting-star streaks — Round 2 only. A bright soft-gold
          trail sweeps across the screen at a random angle and height.
          Makes the deeper wave feel like the heavens are opening. */}
      <AnimatePresence>
        {streaks.map((s) => (
          <motion.div
            key={s.key}
            aria-hidden
            className="absolute pointer-events-none"
            style={{
              top: `${s.top}%`,
              [s.rightToLeft ? "right" : "left"]: "-25%",
              width: "35vw",
              height: "2px",
              transform: `rotate(${s.angle}deg)`,
              background: `linear-gradient(${s.rightToLeft ? "to left" : "to right"}, rgba(255,246,220,0) 0%, rgba(255,236,190,0.35) 35%, rgba(255,246,220,0.95) 75%, rgba(255,255,255,1) 100%)`,
              boxShadow: "0 0 14px rgba(255, 236, 190, 0.9), 0 0 32px rgba(215, 187, 122, 0.5)",
              mixBlendMode: "screen",
            }}
            initial={{ opacity: 0, [s.rightToLeft ? "right" : "left"]: "-25%" }}
            animate={{
              opacity: [0, 1, 1, 0],
              [s.rightToLeft ? "right" : "left"]: "115%",
            }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], times: [0, 0.15, 0.7, 1] }}
            data-testid="rli-immersion-streak"
          />
        ))}
      </AnimatePresence>

      {/* Exhale burst — fires exactly once at t=ROUND_1_END, the
          moment the "gasp inhale" convergence releases and Round 2
          begins. Bigger and richer than the intro bloom — the whole
          screen briefly floods with soft gold. */}
      <AnimatePresence>
        {exhaleAt && (
          <motion.div
            key={exhaleAt}
            aria-hidden
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none rounded-full"
            style={{
              width: "60px",
              height: "60px",
              background: "radial-gradient(circle, rgba(255,246,220,0.98) 0%, rgba(255,220,150,0.75) 25%, rgba(215,187,122,0.35) 55%, rgba(215,187,122,0) 85%)",
              mixBlendMode: "screen",
            }}
            initial={{ scale: 0.3, opacity: 0.9 }}
            animate={{ scale: 28, opacity: [0.9, 0.95, 0] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 2.2, ease: [0.16, 1, 0.3, 1], times: [0, 0.2, 1] }}
            data-testid="rli-immersion-exhale-burst"
          />
        )}
      </AnimatePresence>

      {/* Coloured concentric ripples — the "witches" pulsing outward
          from the dot in Round 2 taking the user deeper. Each ring
          scales from ~40px to ~120vmin over 4s while fading. */}
      <AnimatePresence>
        {ripples.map((r) => (
          <motion.div
            key={r.key}
            aria-hidden
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none rounded-full"
            style={{
              width: "40px",
              height: "40px",
              border: `2px solid ${r.colour}`,
              boxShadow: `0 0 22px ${r.colour}, inset 0 0 22px ${r.colour}`,
              mixBlendMode: "screen",
            }}
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 40, opacity: [0, 0.9, 0.9, 0] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 4, ease: [0.16, 1, 0.3, 1], times: [0, 0.15, 0.55, 1] }}
            data-testid="rli-immersion-ripple"
          />
        ))}
      </AnimatePresence>

      {/* Central gold fixation dot — breathes slowly and swells in
          the final crescendo (last 30s) into a wider halo. */}
      <motion.div
        aria-hidden
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none rounded-full"
        style={{
          width: `${18 + crescendo * 26}px`,
          height: `${18 + crescendo * 26}px`,
          background: "radial-gradient(circle, rgba(255,236,190,1) 0%, rgba(215,187,122,0.9) 45%, rgba(215,187,122,0) 100%)",
          boxShadow: `0 0 ${44 + crescendo * 80}px rgba(255, 220, 150, ${0.9 + crescendo * 0.1}), 0 0 ${96 + crescendo * 200}px rgba(215, 187, 122, ${0.55 + crescendo * 0.35})`,
        }}
        animate={{ scale: [1, 1.18 + crescendo * 0.35, 1] }}
        transition={{ duration: 5.5 - crescendo * 1.5, repeat: Infinity, ease: "easeInOut" }}
        data-testid="rli-immersion-dot"
      />
      </div>

      {/* Affirmation phrase — very subtle, subliminal fade every 7s */}
      <div className="absolute inset-x-0 pointer-events-none flex justify-center" style={{ top: "68%" }}>
        <AnimatePresence mode="wait">
          {currentPhrase && (
            <motion.p
              key={`phrase-${phraseIdx}`}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 0.38, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
              className="italic text-center px-8"
              style={{
                color: TOK.cream,
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "clamp(0.85rem, 2vw, 1.15rem)",
                lineHeight: 1.3,
                letterSpacing: "0.02em",
                maxWidth: "28rem",
                textShadow: "0 0 22px rgba(3, 6, 10, 0.85)",
              }}
              data-testid="rli-immersion-phrase"
            >
              {currentPhrase}
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      {/* Top chrome — title + close (close locked until skipUnlocked) */}
      <div className="absolute top-6 left-6 right-6 flex items-center justify-between pointer-events-none">
        <p
          className="uppercase tracking-[0.4em]"
          style={{
            color: TOK.goldSoft,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.58rem",
            opacity: 0.85,
          }}
        >
          {journey.title}
        </p>
        {skipUnlocked ? (
          <button
            onClick={handleClose}
            className="pointer-events-auto w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: "rgba(15, 27, 23, 0.6)", border: `1px solid ${TOK.goldMist}`, color: TOK.creamDim }}
            aria-label="Close"
            data-testid="rli-immersion-close"
          >
            <X size={16} />
          </button>
        ) : (
          <div
            className="uppercase tracking-[0.28em] px-3 py-2 rounded-full"
            style={{
              background: "rgba(15, 27, 23, 0.45)",
              border: `1px solid ${TOK.goldMist}`,
              color: TOK.creamDim,
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.55rem",
            }}
            data-testid="rli-immersion-lock"
          >
            Stay · {remainingLock}s
          </div>
        )}
      </div>

      {/* Completion veil */}
      <AnimatePresence>
        {isCompleting && (
          <motion.div
            key="rli-completion"
            className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
            style={{
              zIndex: 130,
              background: "radial-gradient(ellipse at 50% 50%, rgba(215, 187, 122, 0.32) 0%, rgba(20, 32, 28, 0.96) 45%, rgba(3, 6, 10, 1) 100%)",
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            data-testid="rli-immersion-completion"
          >
            <motion.p
              className="uppercase tracking-[0.5em] mb-5"
              style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif', fontSize: "0.58rem" }}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.2 }}
            >
              {veilName}
            </motion.p>
            <motion.p
              className="italic"
              style={{
                color: TOK.cream,
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "clamp(2.4rem, 6vw, 3.6rem)",
                fontWeight: 400,
                lineHeight: 1.1,
                textShadow: "0 0 32px rgba(215, 187, 122, 0.5)",
              }}
              initial={{ opacity: 0, letterSpacing: "0.02em" }}
              animate={{ opacity: 1, letterSpacing: "0.005em" }}
              transition={{ duration: 1.4, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
            >
              Installed.
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom controls */}
      <div className="absolute bottom-8 left-0 right-0 flex items-center justify-center gap-3">
        <button
          onClick={() => setIsPaused((p) => !p)}
          className="w-12 h-12 rounded-full flex items-center justify-center transition-all"
          style={{
            background: "rgba(15, 27, 23, 0.7)",
            border: `1px solid ${TOK.gold}`,
            color: TOK.cream,
          }}
          data-testid="rli-immersion-playpause"
        >
          {isPaused ? <Play size={16} className="ml-0.5" /> : <Pause size={16} />}
        </button>
        <button
          onClick={() => setIsMuted((m) => !m)}
          className="w-12 h-12 rounded-full flex items-center justify-center transition-all"
          style={{
            background: "rgba(15, 27, 23, 0.7)",
            border: `1px solid ${TOK.goldMist}`,
            color: TOK.creamDim,
          }}
          data-testid="rli-immersion-mute"
        >
          {isMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
        </button>
      </div>
    </motion.div>
  );
}
